// @ts-nocheck
import { useState, useRef, useEffect, useMemo } from "react";
import {
  useListContacts, useCreateContact, useDeleteContact, getListContactsQueryKey,
  useListContactGroups, useCreateContactGroup, useDeleteContactGroup, getListContactGroupsQueryKey,
  useUpdateContact, useListDevices,
} from "../lib/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "../components/layout/PageHeader";
import { StatCard } from "../components/StatCard";
import { useLang } from "../lib/lang";

const API = import.meta.env.VITE_API_URL ?? "";

const ACCEPTED_EXTS = ".csv,.xlsx,.xls,.ods,.tsv,.txt,.xml,.json";

export function Contacts() {
  const { t } = useLang();
  const d = t.contacts;
  const qc = useQueryClient();
  const { data: contacts, isLoading } = useListContacts();
  const { data: groups } = useListContactGroups();
  const { data: devices } = useListDevices();
  const createContact = useCreateContact();
  const deleteContact = useDeleteContact();
  const createGroup = useCreateContactGroup();
  const deleteGroup = useDeleteContactGroup();
  const updateContact = useUpdateContact();

  const [selectedGroup, setSelectedGroup] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkMoving, setBulkMoving] = useState(false);
  const [moveMenu, setMoveMenu] = useState<number | "bulk" | null>(null);
  const [showContactForm, setShowContactForm] = useState(false);
  const [showGroupForm, setShowGroupForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [cName, setCName] = useState("");
  const [cPhone, setCPhone] = useState("");
  const [cGroupId, setCGroupId] = useState("");
  const [gName, setGName] = useState("");

  // WhatsApp sync import state
  const [showWaImport, setShowWaImport] = useState(false);
  const [waDevice, setWaDevice] = useState("");
  const [waSource, setWaSource] = useState<"all" | "contacts" | "chats">("all");
  const [waContactList, setWaContactList] = useState<Array<{ phone: string; name: string }> | null>(null);
  const [waLoading, setWaLoading] = useState(false);
  const [waSelected, setWaSelected] = useState<Set<string>>(new Set());
  const [waGroupMode, setWaGroupMode] = useState<"new" | "existing">("new");
  const [waNewGroupName, setWaNewGroupName] = useState("");
  const [waExistingGroupId, setWaExistingGroupId] = useState("");
  const [waImporting, setWaImporting] = useState(false);
  const [waImportResult, setWaImportResult] = useState<{ imported: number; skipped: number } | null>(null);
  const [waError, setWaError] = useState<string | null>(null);
  const [waSearch, setWaSearch] = useState("");

  const connectedDevices = useMemo(() => (devices ?? []).filter((d: any) => d.status === "connected"), [devices]);

  const handleWaLoad = async () => {
    if (!waDevice) return;
    setWaLoading(true);
    setWaContactList(null);
    setWaError(null);
    setWaImportResult(null);
    setWaSelected(new Set());
    try {
      const endpoint = waSource === "chats"
        ? `${API}/api/contacts/wa-chats/${waDevice}`
        : waSource === "contacts"
        ? `${API}/api/contacts/wa-sync/${waDevice}`
        : `${API}/api/contacts/wa-all/${waDevice}`;
      const res = await fetch(endpoint);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "Failed");
      const list: Array<{ phone: string; name: string }> = data.contacts ?? [];
      setWaContactList(list);
      setWaSelected(new Set(list.map(c => c.phone)));
    } catch (e: any) {
      setWaError(e.message ?? "Failed to load contacts");
    } finally {
      setWaLoading(false);
    }
  };

  const handleWaImport = async () => {
    if (!waContactList) return;
    const toImport = waContactList.filter(c => waSelected.has(c.phone));
    if (toImport.length === 0) return;
    setWaImporting(true);
    setWaImportResult(null);
    try {
      let groupId: number | undefined;
      if (waGroupMode === "new" && waNewGroupName.trim()) {
        const gr = await createGroup.mutateAsync({ data: { name: waNewGroupName.trim() } } as any);
        groupId = (gr as any).id;
        qc.invalidateQueries({ queryKey: getListContactGroupsQueryKey() });
      } else if (waGroupMode === "existing" && waExistingGroupId) {
        groupId = Number(waExistingGroupId);
      }
      const res = await fetch(`${API}/api/contacts/wa-import`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ deviceId: waDevice, groupId, contacts: toImport }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "Import failed");
      setWaImportResult({ imported: data.imported, skipped: data.skipped });
      qc.invalidateQueries({ queryKey: getListContactsQueryKey() });
      qc.invalidateQueries({ queryKey: getListContactGroupsQueryKey() });
    } catch (e: any) {
      setWaError(e.message ?? "Import failed");
    } finally {
      setWaImporting(false);
    }
  };

  const resetWaImport = () => {
    setWaDevice(""); setWaSource("all"); setWaContactList(null); setWaLoading(false);
    setWaSelected(new Set()); setWaGroupMode("new"); setWaNewGroupName("");
    setWaExistingGroupId(""); setWaImporting(false); setWaImportResult(null);
    setWaError(null); setWaSearch("");
  };

  const waFiltered = useMemo(() => {
    if (!waContactList) return [];
    if (!waSearch) return waContactList;
    const q = waSearch.toLowerCase();
    return waContactList.filter(c => c.name.toLowerCase().includes(q) || c.phone.includes(q));
  }, [waContactList, waSearch]);

  // Import state
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importGroupId, setImportGroupId] = useState("");
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ imported: number; skipped: number } | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const filtered = (contacts ?? []).filter((c) => {
    const matchGroup = selectedGroup === null || c.groupId === selectedGroup;
    const matchSearch = !search || c.name.toLowerCase().includes(search.toLowerCase()) || c.phoneNumber.includes(search);
    return matchGroup && matchSearch;
  });

  const handleGroupChange = (groupId: number | null) => {
    setSelectedGroup(groupId);
    setSelectedIds(new Set());
  };

  const handleCreateContact = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createContact.mutateAsync({ data: { name: cName, phoneNumber: cPhone, groupId: cGroupId ? Number(cGroupId) : undefined } } as any);
      setCName(""); setCPhone(""); setCGroupId(""); setShowContactForm(false);
      qc.invalidateQueries({ queryKey: getListContactsQueryKey() });
    } catch {
      alert("Failed to add contact. Please try again.");
    }
  };

  const handleCreateGroup = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createGroup.mutateAsync({ data: { name: gName } } as any);
      setGName(""); setShowGroupForm(false);
      qc.invalidateQueries({ queryKey: getListContactGroupsQueryKey() });
    } catch {
      alert("Failed to create group. Please try again.");
    }
  };

  const handleDeleteContact = async (id: number) => {
    if (!confirm("Delete this contact?")) return;
    await deleteContact.mutateAsync({ id });
    setSelectedIds(prev => { const s = new Set(prev); s.delete(id); return s; });
    qc.invalidateQueries({ queryKey: getListContactsQueryKey() });
  };

  // Close move menu on outside click
  useEffect(() => {
    if (moveMenu === null) return;
    const close = () => setMoveMenu(null);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [moveMenu]);

  const handleMoveContact = async (contactId: number, groupId: number | null) => {
    setMoveMenu(null);
    await updateContact.mutateAsync({ id: contactId, data: { groupId: groupId ?? undefined } } as any);
    qc.invalidateQueries({ queryKey: getListContactsQueryKey() });
  };

  const handleBulkMove = async (groupId: number | null) => {
    setMoveMenu(null);
    if (selectedIds.size === 0) return;
    setBulkMoving(true);
    try {
      await Promise.all(
        Array.from(selectedIds).map(id =>
          fetch(`${API}/api/contacts/${id}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ groupId }),
          })
        )
      );
      qc.invalidateQueries({ queryKey: getListContactsQueryKey() });
    } finally {
      setBulkMoving(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!confirm(`Delete ${selectedIds.size} selected contact${selectedIds.size > 1 ? "s" : ""}? This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      await fetch(`${API}/api/contacts/bulk-delete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids: Array.from(selectedIds) }),
      });
      setSelectedIds(new Set());
      qc.invalidateQueries({ queryKey: getListContactsQueryKey() });
    } catch {
      alert("Failed to delete contacts.");
    } finally {
      setBulkDeleting(false);
    }
  };

  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  };

  const allFilteredSelected = filtered.length > 0 && filtered.every(c => selectedIds.has(c.id));
  const someSelected = filtered.some(c => selectedIds.has(c.id));

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      setSelectedIds(prev => {
        const s = new Set(prev);
        filtered.forEach(c => s.delete(c.id));
        return s;
      });
    } else {
      setSelectedIds(prev => {
        const s = new Set(prev);
        filtered.forEach(c => s.add(c.id));
        return s;
      });
    }
  };

  const resetImport = () => {
    setImportFile(null);
    setImportGroupId("");
    setImporting(false);
    setImportResult(null);
    setImportError(null);
    setDragOver(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleImportFile = (file: File) => {
    setImportFile(file);
    setImportResult(null);
    setImportError(null);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleImportFile(file);
  };

  const handleImport = async () => {
    if (!importFile) return;
    setImporting(true);
    setImportError(null);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append("file", importFile);
      if (importGroupId) formData.append("groupId", importGroupId);

      const res = await fetch(`${API}/api/contacts/import`, {
        method: "POST",
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) {
        setImportError(data.error ?? "Import failed");
      } else {
        setImportResult({ imported: data.imported, skipped: data.skipped });
        qc.invalidateQueries({ queryKey: getListContactsQueryKey() });
        qc.invalidateQueries({ queryKey: getListContactGroupsQueryKey() });
      }
    } catch {
      setImportError("Upload failed. Please try again.");
    } finally {
      setImporting(false);
    }
  };

  const extIcon = (name: string) => {
    const ext = name.split(".").pop()?.toLowerCase() ?? "";
    if (["xlsx", "xls", "ods"].includes(ext)) return "fas fa-file-excel text-green-600";
    if (ext === "csv") return "fas fa-file-csv text-blue-600";
    if (ext === "xml") return "fas fa-file-code text-orange-500";
    if (ext === "json") return "fas fa-file-code text-yellow-600";
    return "fas fa-file-alt text-[#8898aa]";
  };

  return (
    <div>
      <PageHeader
        title={d.title}
        actions={[
          { label: d.importFromWa, icon: "fab fa-whatsapp", onClick: () => { resetWaImport(); setShowWaImport(true); } },
          { label: d.importContacts, icon: "fas fa-file-import", onClick: () => { resetImport(); setShowImport(true); } },
          { label: d.newGroup, icon: "fas fa-folder-plus", onClick: () => setShowGroupForm(true) },
          { label: d.addContact, icon: "fas fa-plus", onClick: () => setShowContactForm(true) },
        ]}
      />

      <div className="px-6 pb-6 space-y-6">
        <div className="grid grid-cols-3 gap-4">
          <StatCard label={d.totalContacts} value={contacts?.length ?? 0} iconClass="fas fa-address-book" gradient="primary" />
          <StatCard label={d.totalGroups} value={groups?.length ?? 0} iconClass="fas fa-folder" gradient="info" />
          <StatCard label={t.common.active} value={contacts?.length ?? 0} iconClass="fas fa-user-check" gradient="success" />
        </div>

        {/* Import modal */}
        {showImport && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
              <div className="flex items-center justify-between px-6 py-4 border-b border-[#e9ecef]">
                <div>
                  <h5 className="font-semibold text-[#32325d]">{d.importContacts}</h5>
                  <p className="text-xs text-[#8898aa] mt-0.5">{d.csvAutoDetect}</p>
                </div>
                <button onClick={() => { setShowImport(false); resetImport(); }} className="text-[#8898aa] hover:text-[#32325d]">
                  <i className="fas fa-times" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                {/* Drop zone */}
                <div
                  onClick={() => fileInputRef.current?.click()}
                  onDrop={handleDrop}
                  onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
                    dragOver ? "border-[#1171ef] bg-[#eef0fd]" : importFile ? "border-green-400 bg-green-50" : "border-[#cad1d7] hover:border-[#1171ef] hover:bg-[#f8f9fe]"
                  }`}
                >
                  {importFile ? (
                    <div className="flex items-center justify-center gap-3">
                      <i className={`text-3xl ${extIcon(importFile.name)}`} />
                      <div className="text-left">
                        <p className="text-sm font-medium text-[#32325d]">{importFile.name}</p>
                        <p className="text-xs text-[#8898aa]">{(importFile.size / 1024).toFixed(1)} KB</p>
                      </div>
                      <button
                        type="button"
                        onClick={e => { e.stopPropagation(); resetImport(); }}
                        className="ml-2 text-[#cad1d7] hover:text-[#f5365c] text-lg"
                      >
                        <i className="fas fa-times-circle" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <i className="fas fa-cloud-upload-alt text-4xl text-[#8898aa] mb-3 block" />
                      <p className="text-sm font-medium text-[#525f7f]">{d.dragDrop}</p>
                      <p className="text-xs text-[#8898aa] mt-1">{d.fileTypes}</p>
                    </>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPTED_EXTS}
                  className="hidden"
                  onChange={e => { const f = e.target.files?.[0]; if (f) handleImportFile(f); }}
                />

                {/* Group selector */}
                <div>
                  <label className="block text-xs font-semibold text-[#525f7f] mb-1.5">
                    {d.assignToGroup} <span className="font-normal text-[#8898aa]">({t.common.optional})</span>
                  </label>
                  <select
                    value={importGroupId}
                    onChange={e => setImportGroupId(e.target.value)}
                    className="w-full border border-[#cad1d7] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1171ef]/30 focus:border-[#1171ef]"
                  >
                    <option value="">— {d.noGroup} —</option>
                    {groups?.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                  </select>
                </div>

                {/* Info banner */}
                <div className="bg-[#eef0fd] rounded-lg px-4 py-3 text-xs text-[#1171ef]">
                  <i className="fas fa-info-circle mr-1.5" />
                  {d.autoDetect}
                </div>

                {/* Error */}
                {importError && (
                  <div className="bg-[#fff3cd] border border-[#ffc107] text-[#856404] px-4 py-3 rounded-lg text-sm">
                    <i className="fas fa-exclamation-triangle mr-1.5" />{importError}
                  </div>
                )}

                {/* Success result */}
                {importResult && (
                  <div className="bg-[#d4edda] border border-[#c3e6cb] text-[#155724] px-4 py-3 rounded-lg text-sm flex items-center gap-2">
                    <i className="fas fa-check-circle text-lg" />
                    <div>
                      <p className="font-semibold">{d.importedSuccess.replace("{n}", String(importResult.imported))}</p>
                      {importResult.skipped > 0 && <p className="text-xs mt-0.5">{d.skippedRows.replace("{n}", String(importResult.skipped))}</p>}
                    </div>
                  </div>
                )}

                <div className="flex justify-end gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => { setShowImport(false); resetImport(); }}
                    className="px-4 py-2 text-sm border border-[#e9ecef] rounded-lg text-[#525f7f]"
                  >
                    {importResult ? d.close : t.common.cancel}
                  </button>
                  {!importResult && (
                    <button
                      onClick={handleImport}
                      disabled={!importFile || importing}
                      className="px-5 py-2 text-sm gradient-primary text-white rounded-lg disabled:opacity-50 flex items-center gap-2"
                    >
                      {importing ? <><i className="fas fa-spinner fa-spin" />{d.importing}</> : <><i className="fas fa-file-import" />{d.importContacts}</>}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* WhatsApp sync import modal */}
        {showWaImport && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg flex flex-col" style={{ maxHeight: "90vh" }}>
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-[#e9ecef] shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-[#25d366] flex items-center justify-center">
                    <i className="fab fa-whatsapp text-white text-lg" />
                  </div>
                  <div>
                    <h5 className="font-semibold text-[#32325d]">{d.waImportTitle}</h5>
                    <p className="text-xs text-[#8898aa] mt-0.5">{d.waImportDesc}</p>
                  </div>
                </div>
                <button onClick={() => { setShowWaImport(false); resetWaImport(); }} className="text-[#8898aa] hover:text-[#32325d]">
                  <i className="fas fa-times" />
                </button>
              </div>

              <div className="p-6 space-y-4 overflow-y-auto flex-1">
                {/* Source toggle */}
                <div className="flex bg-[#f6f9fc] rounded-lg p-1 gap-1">
                  {(["all", "contacts", "chats"] as const).map(src => (
                    <button
                      key={src}
                      onClick={() => { setWaSource(src); setWaContactList(null); setWaError(null); setWaImportResult(null); }}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-semibold rounded-md transition-colors ${waSource === src ? "bg-white text-[#1171ef] shadow-sm" : "text-[#8898aa] hover:text-[#525f7f]"}`}
                    >
                      <i className={src === "all" ? "fas fa-layer-group" : src === "contacts" ? "fas fa-address-book" : "fas fa-comments"} />
                      {src === "all" ? d.waSourceAll : src === "contacts" ? d.waSourceContacts : d.waSourceChats}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-[#8898aa] -mt-1">
                  {waSource === "all" ? d.waSourceAllDesc : waSource === "contacts" ? d.waSourceContactsDesc : d.waSourceChatsDesc}
                </p>

                {/* Device selector */}
                <div className="flex gap-2">
                  <select
                    value={waDevice}
                    onChange={e => { setWaDevice(e.target.value); setWaContactList(null); setWaError(null); setWaImportResult(null); }}
                    className="flex-1 border border-[#cad1d7] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1171ef]/30 focus:border-[#1171ef]"
                  >
                    <option value="">{d.waSelectDevice}</option>
                    {connectedDevices.map((dv: any) => (
                      <option key={dv.id} value={String(dv.id)}>{dv.name || dv.phoneNumber || `Device #${dv.id}`}</option>
                    ))}
                  </select>
                  <button
                    onClick={handleWaLoad}
                    disabled={!waDevice || waLoading}
                    className="px-4 py-2 text-sm gradient-primary text-white rounded-lg disabled:opacity-40 flex items-center gap-2 shrink-0"
                  >
                    {waLoading ? <><i className="fas fa-circle-notch fa-spin" />{d.waLoading.split("…")[0]}…</> : <><i className="fas fa-sync" />{d.waLoadContacts}</>}
                  </button>
                </div>

                {/* Error */}
                {waError && (
                  <div className="bg-red-50 border border-red-100 rounded-lg px-4 py-2.5 text-sm text-red-600 flex items-center gap-2">
                    <i className="fas fa-exclamation-triangle" />{waError}
                  </div>
                )}

                {/* No contacts */}
                {waContactList !== null && waContactList.length === 0 && (
                  <div className="bg-[#f6f9fc] rounded-xl p-6 text-center">
                    <i className="fab fa-whatsapp text-4xl text-[#cad1d7] mb-3 block" />
                    <p className="text-sm font-semibold text-[#525f7f] mb-1">{d.waNoContacts}</p>
                    <p className="text-xs text-[#8898aa] mb-4">{d.waNoContactsHint}</p>
                    <a
                      href="/wasender/devices"
                      onClick={() => setShowWaImport(false)}
                      className="inline-flex items-center gap-2 bg-[#1171ef] hover:bg-[#4a5dd0] text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors"
                    >
                      <i className="fas fa-mobile-alt" />{d.waGoToDevices}
                    </a>
                  </div>
                )}

                {/* Contact list */}
                {waContactList !== null && waContactList.length > 0 && (
                  <>
                    {/* Stats + search + select all */}
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold text-[#1171ef] bg-[#eef0fd] px-2.5 py-1 rounded-full">
                        {d.waContactsLoaded.replace("{n}", String(waContactList.length))}
                        {waSelected.size < waContactList.length && ` · ${waSelected.size} selected`}
                      </span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setWaSelected(waSelected.size === waFiltered.length ? new Set() : new Set(waFiltered.map(c => c.phone)))}
                          className="text-xs text-[#1171ef] hover:underline"
                        >
                          {waSelected.size === waFiltered.length ? d.waDeselectAll : d.waSelectAll}
                        </button>
                        <div className="relative">
                          <i className="fas fa-search absolute start-2.5 top-1/2 -translate-y-1/2 text-[#adb5bd] text-xs pointer-events-none" />
                          <input
                            value={waSearch}
                            onChange={e => setWaSearch(e.target.value)}
                            placeholder="Search…"
                            className="border border-[#e9ecef] rounded-lg ps-7 pe-2 py-1 text-xs w-36 focus:outline-none focus:ring-1 focus:ring-[#1171ef]"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Scrollable list */}
                    <div className="border border-[#e9ecef] rounded-xl overflow-hidden" style={{ maxHeight: "220px", overflowY: "auto" }}>
                      {waFiltered.length === 0 ? (
                        <div className="p-4 text-center text-xs text-[#8898aa]">No results</div>
                      ) : waFiltered.map(c => {
                        const checked = waSelected.has(c.phone);
                        return (
                          <label
                            key={c.phone}
                            className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-[#f8f9fe] border-b border-[#f6f9fc] last:border-0 transition-colors ${checked ? "bg-[#f0f4ff]" : ""}`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => setWaSelected(prev => {
                                const s = new Set(prev);
                                s.has(c.phone) ? s.delete(c.phone) : s.add(c.phone);
                                return s;
                              })}
                              className="w-4 h-4 accent-[#1171ef] shrink-0"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-[#32325d] truncate">{c.name || c.phone}</p>
                              <p className="text-xs text-[#8898aa]">{c.phone}</p>
                            </div>
                          </label>
                        );
                      })}
                    </div>

                    {/* Group config */}
                    <div className="space-y-2">
                      <label className="block text-xs font-semibold text-[#525f7f]">{d.waImportGroup}</label>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setWaGroupMode("new")}
                          className={`flex-1 py-2 text-xs font-medium rounded-lg border transition-colors ${waGroupMode === "new" ? "border-[#1171ef] bg-[#eef0fd] text-[#1171ef]" : "border-[#e9ecef] text-[#525f7f] hover:border-[#1171ef]"}`}
                        >
                          <i className="fas fa-folder-plus me-1" />{d.waGroupCreate}
                        </button>
                        <button
                          onClick={() => setWaGroupMode("existing")}
                          className={`flex-1 py-2 text-xs font-medium rounded-lg border transition-colors ${waGroupMode === "existing" ? "border-[#1171ef] bg-[#eef0fd] text-[#1171ef]" : "border-[#e9ecef] text-[#525f7f] hover:border-[#1171ef]"}`}
                        >
                          <i className="fas fa-folder me-1" />{d.waExistingGroup}
                        </button>
                      </div>
                      {waGroupMode === "new" ? (
                        <input
                          value={waNewGroupName}
                          onChange={e => setWaNewGroupName(e.target.value)}
                          placeholder={d.waNewGroup}
                          className="w-full border border-[#cad1d7] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1171ef]/30 focus:border-[#1171ef]"
                        />
                      ) : (
                        <select
                          value={waExistingGroupId}
                          onChange={e => setWaExistingGroupId(e.target.value)}
                          className="w-full border border-[#cad1d7] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1171ef]/30 focus:border-[#1171ef]"
                        >
                          <option value="">{d.noGroup}</option>
                          {groups?.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                        </select>
                      )}
                    </div>
                  </>
                )}

                {/* Result */}
                {waImportResult && (
                  <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-2.5 text-sm text-green-700 flex items-center gap-2">
                    <i className="fas fa-check-circle" />
                    {d.waImportDone.replace("{n}", String(waImportResult.imported)).replace("{s}", String(waImportResult.skipped))}
                  </div>
                )}
              </div>

              {/* Footer */}
              {waContactList !== null && waContactList.length > 0 && !waImportResult && (
                <div className="px-6 py-4 border-t border-[#e9ecef] shrink-0 flex justify-end gap-2">
                  <button onClick={() => { setShowWaImport(false); resetWaImport(); }} className="px-4 py-2 text-sm border border-[#e9ecef] rounded-lg text-[#525f7f]">
                    {t.common.cancel}
                  </button>
                  <button
                    onClick={handleWaImport}
                    disabled={waImporting || waSelected.size === 0}
                    className="px-5 py-2 text-sm gradient-primary text-white rounded-lg disabled:opacity-50 flex items-center gap-2"
                  >
                    {waImporting
                      ? <><i className="fas fa-circle-notch fa-spin" />{d.waImporting}</>
                      : <><i className="fab fa-whatsapp" />{d.waImportBtn} ({waSelected.size})</>
                    }
                  </button>
                </div>
              )}
              {waImportResult && (
                <div className="px-6 py-4 border-t border-[#e9ecef] shrink-0 flex justify-end">
                  <button onClick={() => { setShowWaImport(false); resetWaImport(); }} className="px-5 py-2 text-sm gradient-primary text-white rounded-lg">
                    {d.close}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Contact form modal */}
        {showContactForm && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
              <div className="flex items-center justify-between px-6 py-4 border-b border-[#e9ecef]">
                <h5 className="font-semibold text-[#32325d]">{d.addContact}</h5>
                <button onClick={() => setShowContactForm(false)} className="text-[#8898aa] hover:text-[#32325d]"><i className="fas fa-times" /></button>
              </div>
              <form onSubmit={handleCreateContact} className="p-6 space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-[#525f7f] mb-1.5">{d.fullName}</label>
                  <input value={cName} onChange={e => setCName(e.target.value)} className="w-full border border-[#cad1d7] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1171ef]/30 focus:border-[#1171ef]" placeholder={d.namePlaceholder} required />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#525f7f] mb-1.5">{d.contactPhone}</label>
                  <input value={cPhone} onChange={e => setCPhone(e.target.value)} className="w-full border border-[#cad1d7] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1171ef]/30 focus:border-[#1171ef]" placeholder={d.phonePlaceholder} required />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#525f7f] mb-1.5">{d.groupOptional}</label>
                  <select value={cGroupId} onChange={e => setCGroupId(e.target.value)} className="w-full border border-[#cad1d7] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1171ef]/30 focus:border-[#1171ef]">
                    <option value="">{d.noGroup}</option>
                    {groups?.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                  </select>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button type="button" onClick={() => setShowContactForm(false)} className="px-4 py-2 text-sm border border-[#e9ecef] rounded-lg text-[#525f7f]">{t.common.cancel}</button>
                  <button type="submit" disabled={createContact.isPending} className="px-4 py-2 text-sm gradient-primary text-white rounded-lg disabled:opacity-50">
                    {createContact.isPending ? t.common.saving : d.addContact}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Group form modal */}
        {showGroupForm && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm">
              <div className="flex items-center justify-between px-6 py-4 border-b border-[#e9ecef]">
                <h5 className="font-semibold text-[#32325d]">{d.createGroup}</h5>
                <button onClick={() => setShowGroupForm(false)} className="text-[#8898aa] hover:text-[#32325d]"><i className="fas fa-times" /></button>
              </div>
              <form onSubmit={handleCreateGroup} className="p-6 space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-[#525f7f] mb-1.5">{d.groupName}</label>
                  <input value={gName} onChange={e => setGName(e.target.value)} className="w-full border border-[#cad1d7] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1171ef]/30 focus:border-[#1171ef]" placeholder="VIP Customers" required />
                </div>
                <div className="flex justify-end gap-2">
                  <button type="button" onClick={() => setShowGroupForm(false)} className="px-4 py-2 text-sm border border-[#e9ecef] rounded-lg text-[#525f7f]">{t.common.cancel}</button>
                  <button type="submit" disabled={createGroup.isPending} className="px-4 py-2 text-sm gradient-primary text-white rounded-lg disabled:opacity-50">
                    {createGroup.isPending ? t.common.creating : d.createGroup}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Main content */}
        <div className="flex gap-4">
          {/* Groups sidebar */}
          <div className="w-52 shrink-0">
            <div className="bg-white rounded-xl shadow-sm border border-[#e9ecef]">
              <div className="px-4 py-3 border-b border-[#e9ecef]">
                <p className="text-xs font-semibold text-[#32325d] uppercase tracking-wider">{d.groups}</p>
              </div>
              <div className="p-2">
                <button
                  onClick={() => handleGroupChange(null)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center justify-between transition-colors ${selectedGroup === null ? "bg-[#eef0fd] text-[#1171ef] font-medium" : "text-[#525f7f] hover:bg-[#f6f9fc]"}`}
                >
                  <span><i className="fas fa-users mr-2 text-xs" />{d.allContacts}</span>
                  <span className="text-xs bg-[#e9ecef] px-1.5 py-0.5 rounded-full">{contacts?.length ?? 0}</span>
                </button>
                {groups?.map((g) => (
                  <div key={g.id} className="flex items-center group">
                    <button
                      onClick={() => handleGroupChange(g.id)}
                      className={`flex-1 text-left px-3 py-2 rounded-lg text-sm flex items-center justify-between transition-colors ${selectedGroup === g.id ? "bg-[#eef0fd] text-[#1171ef] font-medium" : "text-[#525f7f] hover:bg-[#f6f9fc]"}`}
                    >
                      <span className="truncate"><i className="fas fa-folder mr-2 text-xs" />{g.name}</span>
                      <span className="text-xs bg-[#e9ecef] px-1.5 py-0.5 rounded-full shrink-0">
                        {contacts?.filter(c => c.groupId === g.id).length ?? 0}
                      </span>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Contacts table */}
          <div className="flex-1 min-w-0">
            <div className="bg-white rounded-xl shadow-sm border border-[#e9ecef]">
              <div className="px-5 py-4 border-b border-[#e9ecef] flex items-center gap-3">
                <div className="relative flex-1">
                  <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-[#8898aa] text-sm" />
                  <input
                    value={search} onChange={e => setSearch(e.target.value)}
                    className="w-full border border-[#cad1d7] rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1171ef]/30 focus:border-[#1171ef]"
                    placeholder={d.search}
                  />
                </div>
              </div>

              {isLoading ? (
                <div className="p-8 text-center text-[#8898aa]">
                  <i className="fas fa-spinner fa-spin text-2xl mb-2 block" />{t.common.loading}
                </div>
              ) : filtered.length === 0 ? (
                <div className="p-12 text-center">
                  <i className="fas fa-address-book text-4xl text-[#cad1d7] mb-3 block" />
                  <p className="text-[#8898aa] text-sm">{d.noContacts}</p>
                  <button
                    onClick={() => { resetImport(); setShowImport(true); }}
                    className="gradient-primary text-white px-5 py-2 rounded-lg text-sm mt-4 shadow-sm"
                  >
                    <i className="fas fa-file-import mr-2" />{d.importContacts}
                  </button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  {/* Bulk action bar */}
                  {selectedIds.size > 0 && (
                    <div className="flex items-center justify-between px-5 py-2.5 bg-[#eef0fd] border-b border-[#1171ef]/20">
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-[#1171ef]">
                          <i className="fas fa-check-square mr-2" />{selectedIds.size} {d.contactsSelected}
                        </span>
                        <button
                          onClick={() => setSelectedIds(new Set())}
                          className="text-xs text-[#8898aa] hover:text-[#525f7f] underline"
                        >
                          {d.deselectAll}
                        </button>
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Bulk move dropdown */}
                        <div className="relative" onMouseDown={e => e.stopPropagation()}>
                          <button
                            onClick={() => setMoveMenu(prev => prev === "bulk" ? null : "bulk")}
                            disabled={bulkMoving}
                            className="flex items-center gap-2 px-4 py-1.5 rounded-lg bg-white border border-[#1171ef] text-[#1171ef] text-sm font-medium hover:bg-[#eef0fd] transition-colors disabled:opacity-50"
                          >
                            {bulkMoving
                              ? <><i className="fas fa-circle-notch fa-spin" />{d.moving}</>
                              : <><i className="fas fa-folder-open" />{d.moveBulk}</>
                            }
                            <i className="fas fa-chevron-down text-xs" />
                          </button>
                          {moveMenu === "bulk" && (
                            <div className="absolute end-0 top-full mt-1 z-50 bg-white border border-[#e9ecef] rounded-xl shadow-lg min-w-[180px] py-1 overflow-hidden">
                              <div className="px-3 py-1.5 text-[10px] font-semibold text-[#8898aa] uppercase tracking-wider border-b border-[#f6f9fc]">
                                {d.moveToGroup}
                              </div>
                              <button
                                onMouseDown={e => { e.stopPropagation(); handleBulkMove(null); }}
                                className="w-full text-left px-3 py-2 text-sm text-[#525f7f] hover:bg-[#f6f9fc] flex items-center gap-2"
                              >
                                <i className="fas fa-ban text-xs text-[#cad1d7]" />{d.noGroup}
                              </button>
                              {groups?.map(g => (
                                <button
                                  key={g.id}
                                  onMouseDown={e => { e.stopPropagation(); handleBulkMove(g.id); }}
                                  className="w-full text-left px-3 py-2 text-sm text-[#525f7f] hover:bg-[#f6f9fc] flex items-center gap-2"
                                >
                                  <i className="fas fa-folder text-xs text-[#1171ef]" />{g.name}
                                  <span className="ms-auto text-xs text-[#adb5bd]">{contacts?.filter(c => c.groupId === g.id).length ?? 0}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        <button
                          onClick={handleBulkDelete}
                          disabled={bulkDeleting}
                          className="flex items-center gap-2 px-4 py-1.5 rounded-lg bg-[#f5365c] text-white text-sm font-medium hover:bg-[#d32e52] transition-colors disabled:opacity-50"
                        >
                          {bulkDeleting
                            ? <><i className="fas fa-circle-notch fa-spin" />{d.deleting}</>
                            : <><i className="fas fa-trash" />{d.deleteSelected} ({selectedIds.size})</>
                          }
                        </button>
                      </div>
                    </div>
                  )}
                  <table className="w-full">
                    <thead>
                      <tr className="text-left">
                        <th className="px-5 py-3 border-b border-[#e9ecef] w-10">
                          <input
                            type="checkbox"
                            checked={allFilteredSelected}
                            ref={el => { if (el) el.indeterminate = someSelected && !allFilteredSelected; }}
                            onChange={toggleSelectAll}
                            className="w-4 h-4 rounded border-[#cad1d7] text-[#1171ef] cursor-pointer accent-[#1171ef]"
                            title={allFilteredSelected ? "Deselect all" : "Select all"}
                          />
                        </th>
                        <th className="px-5 py-3 text-xs font-semibold text-[#8898aa] uppercase tracking-wider border-b border-[#e9ecef]">{d.contactName}</th>
                        <th className="px-5 py-3 text-xs font-semibold text-[#8898aa] uppercase tracking-wider border-b border-[#e9ecef]">{d.contactPhone}</th>
                        <th className="px-5 py-3 text-xs font-semibold text-[#8898aa] uppercase tracking-wider border-b border-[#e9ecef]">{d.contactGroup}</th>
                        <th className="px-5 py-3 text-xs font-semibold text-[#8898aa] uppercase tracking-wider border-b border-[#e9ecef]">{t.common.actions}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((c) => {
                        const initials = c.name.split(" ").map((w: string) => w[0]).join("").slice(0, 2).toUpperCase();
                        const group = groups?.find(g => g.id === c.groupId);
                        const isSelected = selectedIds.has(c.id);
                        return (
                          <tr
                            key={c.id}
                            className={`border-b border-[#f6f9fc] hover:bg-[#f8f9fe] transition-colors ${isSelected ? "bg-[#f0f4ff]" : ""}`}
                          >
                            <td className="px-5 py-3">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleSelect(c.id)}
                                className="w-4 h-4 rounded border-[#cad1d7] cursor-pointer accent-[#1171ef]"
                              />
                            </td>
                            <td className="px-5 py-3">
                              <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0 ${isSelected ? "bg-[#1171ef]" : "gradient-primary"}`}>
                                  {isSelected ? <i className="fas fa-check text-[10px]" /> : initials}
                                </div>
                                <span className="text-sm font-medium text-[#32325d]">{c.name}</span>
                              </div>
                            </td>
                            <td className="px-5 py-3 text-sm text-[#525f7f]">{c.phoneNumber}</td>
                            <td className="px-5 py-3">
                              {group && (
                                <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-[#eef0fd] text-[#1171ef]">
                                  {group.name}
                                </span>
                              )}
                            </td>
                            <td className="px-5 py-3">
                              <div className="flex items-center gap-3">
                                {/* Move to group */}
                                <div className="relative" onMouseDown={e => e.stopPropagation()}>
                                  <button
                                    onClick={() => setMoveMenu(prev => prev === c.id ? null : c.id)}
                                    className="text-[#cad1d7] hover:text-[#1171ef] text-sm transition-colors"
                                    title={d.moveToGroup}
                                  >
                                    <i className="fas fa-folder-open" />
                                  </button>
                                  {moveMenu === c.id && (
                                    <div className="absolute end-0 top-full mt-1 z-50 bg-white border border-[#e9ecef] rounded-xl shadow-lg min-w-[170px] py-1 overflow-hidden">
                                      <div className="px-3 py-1.5 text-[10px] font-semibold text-[#8898aa] uppercase tracking-wider border-b border-[#f6f9fc]">
                                        {d.moveToGroup}
                                      </div>
                                      <button
                                        onMouseDown={e => { e.stopPropagation(); handleMoveContact(c.id, null); }}
                                        className="w-full text-left px-3 py-2 text-sm text-[#525f7f] hover:bg-[#f6f9fc] flex items-center gap-2"
                                      >
                                        <i className="fas fa-ban text-xs text-[#cad1d7]" />{d.noGroup}
                                      </button>
                                      {groups?.map(g => (
                                        <button
                                          key={g.id}
                                          onMouseDown={e => { e.stopPropagation(); handleMoveContact(c.id, g.id); }}
                                          className={`w-full text-left px-3 py-2 text-sm hover:bg-[#f6f9fc] flex items-center gap-2 ${c.groupId === g.id ? "text-[#1171ef] font-medium bg-[#f0f4ff]" : "text-[#525f7f]"}`}
                                        >
                                          <i className={`fas fa-folder text-xs ${c.groupId === g.id ? "text-[#1171ef]" : "text-[#1171ef]/60"}`} />
                                          {g.name}
                                          {c.groupId === g.id && <i className="fas fa-check text-xs ms-auto text-[#1171ef]" />}
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                <button
                                  onClick={() => handleDeleteContact(c.id)}
                                  className="text-[#cad1d7] hover:text-[#f5365c] text-sm transition-colors"
                                  title={t.common.delete}
                                >
                                  <i className="fas fa-trash" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
